import React, { useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  CalendarIcon,
  ChevronRightIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/solid";
import dayjs from "../../utils/dayjs";
import tw from "tailwind-styled-components";
import styled from "styled-components";

import { renameCircleTitle, removeHuman } from "../../actions/circles";
import HumanAvatar from "./HumanAvatar";
import MemberListModal from "./MemberListModal";
import AddMemeberModal from "./AddMemberModal";
import Input from "../../components/Input";

const CircleLi = styled.li`
  :hover .delete-circle {
    /* text-gray-500 */
    color: rgba(107, 114, 128, var(--tw-text-opacity));
  }
`;

const RemainingCircleMemberCount = tw.span`
inline-flex 
items-center 
justify-center 
h-8
w-8 
rounded-full  
ring-2 
ring-white 
bg-gray-500 
cursor-pointer
`;

const AddMemberBtn = tw.span`
inline-flex 
items-center 
justify-center 
h-8
w-8 
rounded-full  
ring-2 
ring-white 
bg-sky-700
text-white
cursor-pointer
`;

const MemeberListWrapper = tw.div`
mt-4 
flex-shrink-0 
sm:mt-0 
sm:ml-5
`;

function Circle(props) {
  const {
    circleData,
    onRemoveCircle,
    addHumans,
    renameCircleTitle,
    humans,
    removeHuman,
  } = props;
  const [editingTitle, setEditingTitle] = useState(false);
  const [openMemberListModal, setOpenMemberListModal] = useState(false);
  const [openAddNewMember, setOpenAddNewMember] = useState(false);

  const onTitleChange = useCallback(
    (e) => {
      renameCircleTitle(circleData.id, e.target.value);
    },
    [circleData.id, renameCircleTitle]
  );

  const circleMemberList = useMemo(() => {
    if (circleData.humanIds) {
      return humans.filter((human) =>
        circleData.humanIds.some((h) => human.id === h)
      );
    }
    return [];
  }, [humans, circleData]);

  return (
    <CircleLi
      onClick={() => setOpenMemberListModal(true)}
      className="block cursor-pointer"
    >
      <div className="px-4 py-4 flex items-center sm:px-6">
        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="truncate">
            <div className="flex text-sm">
              {!editingTitle ? (
                <p
                  className="font-medium text-sky-600 truncate"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingTitle(true);
                  }}
                >
                  {circleData.name}
                </p>
              ) : (
                <Input
                  className="mt-1 ml-1 mr-2 w-full"
                  type="text"
                  onClick={(e) => e.stopPropagation()}
                  value={circleData.name}
                  style={{ maxWidth: 500 }}
                  onBlur={() => setEditingTitle(false)}
                  onKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setEditingTitle(false);
                    }
                  }}
                  autoFocus
                  onChange={onTitleChange}
                />
              )}
              <div className="flex items-center">
                <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                  {circleData.humanIds.length} human
                  {circleData.humanIds.length !== 1 ? "s" : ""}
                </p>
                <TrashIcon
                  className="text-gray-100 delete-circle h-5 w-5 cursor-pointer ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveCircle();
                  }}
                />
              </div>
            </div>
            <div className="mt-2 flex">
              <div className="flex items-center text-xs text-gray-500">
                <CalendarIcon
                  className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400"
                  aria-hidden="true"
                />
                <p>
                  Last updated{" "}
                  <time>
                    {dayjs(circleData.modifiedAt).format("MMM Do, YYYY")}
                  </time>
                </p>
              </div>
            </div>
          </div>

          <MemeberListWrapper className="mt-4 flex-shrink-0 sm:mt-0 sm:ml-5">
            <div className="flex overflow-hidden -space-x-1">
              {circleMemberList.slice(0, 6).map((human) => (
                <HumanAvatar
                  key={human.id}
                  name={human.name}
                  humanId={human.id}
                  image={human.image}
                />
              ))}
              {circleMemberList.length > 6 && (
                <RemainingCircleMemberCount
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMemberListModal(true);
                  }}
                >
                  <span className="text-xs pr-1 font-medium leading-none text-white">
                    +{circleMemberList.length - 6}
                  </span>
                </RemainingCircleMemberCount>
              )}
              <AddMemberBtn
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenAddNewMember(true);
                }}
              >
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
              </AddMemberBtn>
            </div>
          </MemeberListWrapper>
        </div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            setOpenMemberListModal(true);
          }}
          className="ml-5 flex-shrink-0"
        >
          <ChevronRightIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>
      </div>
      {openMemberListModal && (
        <MemberListModal
          members={circleMemberList}
          closeModal={() => setOpenMemberListModal(false)}
          circleName={circleData.name}
          removeHuman={removeHuman}
          circleId={circleData.id}
        />
      )}
      {openAddNewMember && (
        <AddMemeberModal
          humans={humans}
          closeModal={() => setOpenAddNewMember(false)}
          currentCircle={circleData}
          addHumans={addHumans}
        />
      )}
    </CircleLi>
  );
}

const mapDispatchToProps = (dispatch) => ({
  removeHuman: (humanId, circleId) => dispatch(removeHuman(humanId, circleId)),
  renameCircleTitle: (circleId, name) =>
    dispatch(renameCircleTitle(circleId, name)),
});

const mapStateToProps = (state) => ({
  humans: state.humans,
});

Circle.propTypes = {
  onRemoveCircle: PropTypes.func.isRequired,
  addHumans: PropTypes.func.isRequired,
  circleData: PropTypes.any.isRequired,
  renameCircleTitle: PropTypes.any.isRequired,
  humans: PropTypes.any.isRequired,
  removeHuman: PropTypes.any.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Circle);
